import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/Layout/MainLayout';
import LeaveService from '../services/leaveService';
import LeaveBalanceService from '../services/leaveBalanceService';
import LeaveApplicationModal from '../components/LeaveApplicationModal';

const LeaveManagementPage = () => {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchLeaves();
    fetchStats();
    fetchLeaveBalances();
  }, [filters]);

  const fetchLeaves = async () => {
    try {
      setLoading(true);
      const response = await LeaveService.getMyLeaves(filters);
      setLeaves(response.data);
    } catch (error) {
      setError(error.error || 'Failed to fetch leave requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await LeaveService.getLeaveStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch leave stats:', error);
    }
  };

  const fetchLeaveBalances = async () => {
    try {
      const response = await LeaveBalanceService.getMyLeaveBalances();
      setLeaveBalances(response.data);
    } catch (error) {
      console.error('Failed to fetch leave balances:', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      startDate: '',
      endDate: ''
    });
  };

  const handleApplicationSuccess = () => {
    fetchLeaves();
    fetchStats();
    fetchLeaveBalances();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { class: 'status-pending', text: 'Pending' },
      APPROVED: { class: 'status-approved', text: 'Approved' },
      REJECTED: { class: 'status-rejected', text: 'Rejected' }
    };
    
    const config = statusConfig[status] || { class: 'status-unknown', text: status };
    return <span className={`status-badge ${config.class}`}>{config.text}</span>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateDuration = (leave) => {
    if (leave.durationType === 'HALF_DAY') {
      return '0.5 day';
    }
    
    return `${leave.totalDays} day${leave.totalDays > 1 ? 's' : ''}`;
  };

  if (!['HR', 'CHAIRMAN'].includes(user?.role)) {
    return (
      <MainLayout>
        <div className="page-container">
          <div className="error-message">
            Access denied. Only HR staff and Chairman can access this page.
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="page-container">
        <div className="page-header">
          <h1>Leave Management</h1>
          <button
            className="btn btn-primary"
            onClick={() => setShowApplicationModal(true)}
          >
            Apply for Leave
        </button>
      </div>

      {/* Leave Balances Section */}
      <div className="leave-balances-section">
        <h2>My Leave Balances</h2>
        {leaveBalances.length === 0 ? (
          <div className="empty-state">
            <p>No leave balances assigned. Please contact HRO.</p>
          </div>
        ) : (
          <div className="balance-cards">
            {leaveBalances.map((balance) => (
              <div key={balance._id} className="balance-card">
                <div className="balance-header">
                  <h3>{balance.leaveTypeId.name}</h3>
                  <div
                    className="leave-type-color"
                    style={{ backgroundColor: balance.leaveTypeId.color }}
                  ></div>
                </div>
                <div className="balance-info">
                  <div className="balance-stat">
                    <span className="label">Remaining</span>
                    <span className="value">{balance.remainingLeaves}</span>
                  </div>
                  <div className="balance-stat">
                    <span className="label">Total</span>
                    <span className="value">{balance.totalLeaves}</span>
                  </div>
                  <div className="balance-stat">
                    <span className="label">Used</span>
                    <span className="value">{balance.usedLeaves}</span>
                  </div>
                </div>
                <div className="balance-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${(balance.usedLeaves / balance.totalLeaves) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Pending Requests</h3>
          <div className="stat-value">{stats.pending || 0}</div>
          <div className="stat-label">{stats.pendingDays || 0} days</div>
        </div>
        <div className="stat-card">
          <h3>Approved Leaves</h3>
          <div className="stat-value">{stats.approved || 0}</div>
          <div className="stat-label">{stats.approvedDays || 0} days</div>
        </div>
        <div className="stat-card">
          <h3>Rejected Requests</h3>
          <div className="stat-value">{stats.rejected || 0}</div>
        </div>
        <div className="stat-card">
          <h3>Total Leave Days</h3>
          <div className="stat-value">{stats.totalDays || 0}</div>
          <div className="stat-label">This year</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="startDate">From Date</label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="endDate">To Date</label>
            <input
              type="date"
              id="endDate"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
            />
          </div>
          <button className="btn btn-secondary" onClick={clearFilters}>
            Clear Filters
          </button>
        </div>
      </div>

      {/* Leave Requests Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading-message">Loading leave requests...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : leaves.length === 0 ? (
          <div className="empty-state">
            <p>No leave requests found.</p>
            <button
              className="btn btn-primary"
              onClick={() => setShowApplicationModal(true)}
            >
              Apply for Your First Leave
            </button>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Date(s)</th>
                <th>Duration</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Applied On</th>
                <th>Processed By</th>
              </tr>
            </thead>
            <tbody>
              {leaves.map((leave) => (
                <tr key={leave._id}>
                  <td>
                    <div className="leave-type-info">
                      <div
                        className="leave-type-indicator"
                        style={{ backgroundColor: leave.leaveTypeId?.color || '#6B7280' }}
                      ></div>
                      <div>
                        <div>{leave.leaveTypeId?.name || 'Unknown'}</div>
                        <small className={`duration-badge ${leave.durationType?.toLowerCase() || 'unknown'}`}>
                          {leave.durationType === 'HALF_DAY' ? 'Half Day' : 
                           leave.durationType === 'FULL_DAY' ? 'Full Day' : 'Unknown'}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td>
                    {formatDate(leave.startDate)}
                    {leave.endDate && leave.endDate !== leave.startDate && (
                      <> - {formatDate(leave.endDate)}</>
                    )}
                  </td>
                  <td>{calculateDuration(leave)}</td>
                  <td className="reason-cell" title={leave.reason}>
                    {leave.reason.length > 50 
                      ? `${leave.reason.substring(0, 50)}...` 
                      : leave.reason
                    }
                  </td>
                  <td>{getStatusBadge(leave.status)}</td>
                  <td>{formatDate(leave.createdAt)}</td>
                  <td>
                    {leave.approvedBy ? (
                      <div>
                        <div>{leave.approvedBy.phoneNumber}</div>
                        <small>
                          {leave.approvedAt && formatDate(leave.approvedAt)}
                          {leave.rejectedAt && formatDate(leave.rejectedAt)}
                        </small>
                        {leave.rejectionReason && (
                          <div className="rejection-reason" title={leave.rejectionReason}>
                            Reason: {leave.rejectionReason.length > 30 
                              ? `${leave.rejectionReason.substring(0, 30)}...` 
                              : leave.rejectionReason
                            }
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted">Pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Leave Application Modal */}
      <LeaveApplicationModal
        isOpen={showApplicationModal}
        onClose={() => setShowApplicationModal(false)}
        onSuccess={handleApplicationSuccess}
      />
    </div>
    </MainLayout>
  );
};

export default LeaveManagementPage;